import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from 'antd'
import {
  CommentOutlined,
  EditOutlined,
  ExpandAltOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import type { DocWritingAction } from '../api/docWritingStream'
import './DocSelectionAiToolbar.css'

export type SelectionAiAction = Extract<DocWritingAction, 'polish' | 'expand' | 'continue'>

const ACTIONS: { action: SelectionAiAction; label: string; icon: ReactNode; title: string }[] = [
  { action: 'polish', label: '润色', icon: <EditOutlined />, title: '润色选中内容' },
  { action: 'expand', label: '扩写', icon: <ExpandAltOutlined />, title: '扩写选中内容' },
  { action: 'continue', label: '续写', icon: <PlusOutlined />, title: '在选中内容后继续写' },
]

type Props = {
  containerRef: React.RefObject<HTMLElement | null>
  getSelectionText: () => string
  onAction: (action: SelectionAiAction, selection: string, selectionPlain?: string) => void
  onDescribeIntent: (selection: string, selectionPlain?: string) => void
  disabled?: boolean
}

type ToolbarPos = { top: number; left: number }

function selectionInContainer(
  container: HTMLElement | null,
  getSelectionText: () => string,
): Selection | null {
  if (!container) return null
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null
  const range = sel.getRangeAt(0)
  if (!container.contains(range.commonAncestorContainer)) return null
  const text =
    getSelectionText().trim() || sel.toString().replace(/\u200b/g, '').trim()
  if (!text) return null
  return sel
}

function unionSelectionRect(sel: Selection): DOMRect | null {
  const range = sel.getRangeAt(0)
  const rects = range.getClientRects()
  if (rects.length > 0) {
    let top = Infinity
    let left = Infinity
    let bottom = -Infinity
    let right = -Infinity
    for (let i = 0; i < rects.length; i += 1) {
      const r = rects[i]
      if (r.width === 0 && r.height === 0) continue
      top = Math.min(top, r.top)
      left = Math.min(left, r.left)
      bottom = Math.max(bottom, r.bottom)
      right = Math.max(right, r.right)
    }
    if (top !== Infinity) {
      return new DOMRect(left, top, right - left, bottom - top)
    }
  }
  return null
}

function selectionAnchorRect(sel: Selection): DOMRect | null {
  const range = sel.getRangeAt(0)
  const anchor = range.cloneRange()
  anchor.collapse(true)
  const rect = anchor.getBoundingClientRect()
  if (rect.width || rect.height) return rect
  return selectionRect(sel)
}

function selectionRect(sel: Selection): DOMRect | null {
  const union = unionSelectionRect(sel)
  if (union && (union.width || union.height)) return union

  const range = sel.getRangeAt(0)
  const rect = range.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) {
    const clone = range.cloneRange()
    clone.collapse(true)
    const marker = document.createElement('span')
    marker.textContent = '\u200b'
    clone.insertNode(marker)
    const r = marker.getBoundingClientRect()
    marker.remove()
    return r.width || r.height ? r : null
  }
  return rect
}

export function DocSelectionAiToolbar({
  containerRef,
  getSelectionText,
  onAction,
  onDescribeIntent,
  disabled,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const savedSelectionRef = useRef('')
  const savedSelectionPlainRef = useRef('')
  const selectingRef = useRef(false)
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState<ToolbarPos>({ top: 0, left: 0 })

  const reposition = useCallback(() => {
    if (selectingRef.current) return

    const container = containerRef.current
    const sel = selectionInContainer(container, getSelectionText)
    if (!sel) {
      setVisible(false)
      return
    }
    const rect = selectionRect(sel)
    if (!rect) {
      setVisible(false)
      return
    }
    savedSelectionRef.current =
      getSelectionText().trim() || sel.toString().replace(/\u200b/g, '').trim()
    savedSelectionPlainRef.current = sel.toString().replace(/\u200b/g, '').trim() || savedSelectionRef.current
    const anchor = selectionAnchorRect(sel) ?? rect
    const wrapW = wrapRef.current?.offsetWidth ?? 380
    const wrapH = wrapRef.current?.offsetHeight ?? 40
    let left = anchor.left
    let top = anchor.top - wrapH - 8
    const margin = 8
    if (left + wrapW > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - wrapW - margin)
    }
    left = Math.max(margin, left)
    if (top < margin) top = anchor.bottom + 8
    setPos({ top, left })
    setVisible(true)
  }, [containerRef, getSelectionText])

  const showAfterSelection = useCallback(() => {
    requestAnimationFrame(reposition)
  }, [reposition])

  useEffect(() => {
    const container = containerRef.current
    if (!container || disabled) return

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapRef.current?.contains(target)) return
      if (!container.contains(target)) return
      selectingRef.current = true
      setVisible(false)
    }

    const onMouseUp = () => {
      if (selectingRef.current) selectingRef.current = false
      showAfterSelection()
    }

    const onKeyUp = (e: KeyboardEvent) => {
      const key = e.key
      const selectAll = (e.ctrlKey || e.metaKey) && key.toLowerCase() === 'a'
      if (
        selectAll ||
        e.shiftKey ||
        key === 'Shift' ||
        key.startsWith('Arrow') ||
        key === 'Home' ||
        key === 'End' ||
        key === 'PageUp' ||
        key === 'PageDown'
      ) {
        selectingRef.current = false
        if (selectAll) {
          requestAnimationFrame(() => requestAnimationFrame(showAfterSelection))
        } else {
          showAfterSelection()
        }
      }
    }

    container.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mouseup', onMouseUp)
    container.addEventListener('keyup', onKeyUp)
    document.addEventListener('keyup', onKeyUp)

    const onScroll = () => {
      if (visible) reposition()
    }
    container.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)

    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapRef.current?.contains(target)) return
      if (container.contains(target)) return
      setVisible(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)

    return () => {
      container.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mouseup', onMouseUp)
      container.removeEventListener('keyup', onKeyUp)
      document.removeEventListener('keyup', onKeyUp)
      container.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('mousedown', onDocMouseDown)
    }
  }, [containerRef, disabled, reposition, showAfterSelection, visible])

  useEffect(() => {
    if (visible) requestAnimationFrame(reposition)
  }, [visible, reposition])

  const closeAll = () => {
    setVisible(false)
  }

  const handleAction = (action: SelectionAiAction) => {
    const text = savedSelectionRef.current || getSelectionText().trim()
    if (!text) return
    const plain = savedSelectionPlainRef.current || text
    onAction(action, text, plain)
    closeAll()
  }

  const handleDescribeIntent = () => {
    const text = savedSelectionRef.current || getSelectionText().trim()
    if (!text) return
    const plain = savedSelectionPlainRef.current || text
    onDescribeIntent(text, plain)
    closeAll()
  }

  if (!visible || disabled) return null

  return createPortal(
    <div ref={wrapRef} className="doc-selection-ai-wrap" style={{ top: pos.top, left: pos.left }}>
      <div className="doc-selection-ai-toolbar" onMouseDown={(e) => e.preventDefault()}>
        <span className="doc-selection-ai-toolbar-brand">
          <ThunderboltOutlined />
          AI
        </span>
        {ACTIONS.map((a) => (
          <Button
            key={a.action}
            type="text"
            size="small"
            className="doc-selection-ai-toolbar-btn"
            icon={a.icon}
            title={a.title}
            onClick={() => handleAction(a.action)}
          >
            {a.label}
          </Button>
        ))}
        <Button
          type="text"
          size="small"
          className="doc-selection-ai-toolbar-btn doc-selection-ai-toolbar-btn--intent"
          icon={<CommentOutlined />}
            title="在光标旁打开描述意图对话"
          onMouseDown={(e) => {
            e.preventDefault()
            const text = savedSelectionRef.current || getSelectionText().trim()
            if (text) savedSelectionRef.current = text
          }}
          onClick={handleDescribeIntent}
        >
          描述意图
        </Button>
      </div>
    </div>,
    document.body,
  )
}
